/**
 * Sample bundle used until the worker API is wired up (phase 6). Shapes come
 * from @circle/shared so swapping in a real GET /api/calendars/:id is a no-op.
 */
import type { CalendarBundle, CalEvent, Calendar, Source, Tag, View } from '@circle/shared';

const CAL_ID = 'cal_smpl00000000000001';

/**
 * The circles of docs/CIRCLES.md §1, as a tag tree: three at the top level and
 * one nested, with Family louder than everyone and Work quieter. Prominence is
 * multiplied down the path, so a birthday in Kids carries 1.4 × 1.0 before its
 * own weight is applied.
 */
export const TAG_FAMILY = 'tag_family0000000000001';
export const TAG_FRIENDS = 'tag_friends000000000001';
export const TAG_WORK = 'tag_work00000000000001';
export const TAG_KIDS = 'tag_kids00000000000001';

export const sampleTags: Tag[] = [
  {
    id: TAG_FAMILY,
    calendarId: CAL_ID,
    parentId: null,
    name: 'Family',
    colour: '#c98f4b',
    prominence: 1.4,
    sort: 0,
  },
  {
    id: TAG_FRIENDS,
    calendarId: CAL_ID,
    parentId: null,
    name: 'Friends',
    colour: '#7fce9b',
    prominence: 1,
    sort: 1,
  },
  {
    id: TAG_WORK,
    calendarId: CAL_ID,
    parentId: null,
    name: 'Work',
    colour: '#8494a6',
    prominence: 0.7,
    sort: 2,
  },
  {
    id: TAG_KIDS,
    calendarId: CAL_ID,
    parentId: TAG_FAMILY,
    name: 'Kids',
    prominence: 1,
    sort: 0,
  },
];

export const sampleSources: Source[] = [
  {
    id: 'src_manual0000000000001',
    calendarId: CAL_ID,
    kind: 'manual',
    name: 'Manual',
    colour: '#d8b26a',
  },
  {
    id: 'src_ics000000000000001',
    calendarId: CAL_ID,
    kind: 'ics',
    name: 'Family (ICS)',
    colour: '#6aa9d8',
    url: 'https://example.com/family.ics',
    refreshHours: 24,
    lastFetchedAt: '2026-01-04T04:00:00.000Z',
    // What an imported event is filed into on insert, never on refresh.
    defaultTagId: TAG_WORK,
  },
  {
    id: 'src_collect0000000001',
    calendarId: CAL_ID,
    kind: 'collect',
    name: 'Studio crew',
    colour: '#7fce9b',
  },
];

const MANUAL = sampleSources[0]!.id;
const ICS = sampleSources[1]!.id;
const COLLECT = sampleSources[2]!.id;

interface Seed {
  title: string;
  month: number;
  day: number;
  year?: number;
  source: string;
  kind?: CalEvent['kind'];
  /** Circles this one is in. Absent means the root, "Everyone", and only that. */
  tags?: string[];
}

/**
 * 25 birthdays and 3 notes. Deliberately lumpy: four in the second week of
 * March, three around midsummer and a pair in November, so clustering has
 * something to chew on in phase 3.
 *
 * A third of them are in a circle, two in two circles at once (Tove is Friends
 * and Work, Viktor is Family and Work) so a merged ring has membership dots to
 * draw and a split one has two rings to draw them on. The circles all sit in
 * the second half of the year on purpose: the lumps that clustering is tuned
 * against — March, midsummer — stay in no circle at all, so they keep reading
 * as one crowd on one ring.
 */
const SEEDS: Seed[] = [
  { title: 'Anna Lindqvist', month: 1, day: 4, year: 1986, source: MANUAL },
  { title: 'Bo Hedlund', month: 1, day: 19, year: 1954, source: ICS },
  { title: 'Cecilia Ó Ruairc', month: 2, day: 2, year: 1991, source: ICS },
  { title: 'Dmitri Sokolov', month: 2, day: 29, year: 1996, source: MANUAL },
  { title: 'Elin Vestergaard', month: 3, day: 10, year: 1978, source: COLLECT },
  { title: 'Fatima Haddad', month: 3, day: 11, year: 2001, source: COLLECT },
  { title: 'Gustav Wren', month: 3, day: 12, year: 1949, source: ICS },
  { title: 'Hanna Mbeki', month: 3, day: 14, year: 1993, source: COLLECT },
  { title: 'Ivar Sandgren', month: 4, day: 6, year: 1967, source: MANUAL },
  { title: 'Jun Watanabe', month: 4, day: 23, source: COLLECT, tags: [TAG_KIDS] },
  { title: 'Klara Novák', month: 5, day: 1, year: 1988, source: ICS, tags: [TAG_WORK] },
  { title: 'Liam O’Donnell', month: 5, day: 17, year: 1974, source: MANUAL, tags: [TAG_FAMILY] },
  { title: 'Marta Reyes', month: 6, day: 20, year: 1999, source: COLLECT },
  { title: 'Nils Aakre', month: 6, day: 21, year: 1962, source: ICS },
  { title: 'Oona Virtanen', month: 6, day: 22, year: 1985, source: COLLECT },
  { title: 'Petra Ambrož', month: 7, day: 9, year: 1990, source: MANUAL, tags: [TAG_FRIENDS] },
  { title: 'Quentin Marchal', month: 7, day: 28, year: 1971, source: ICS, tags: [TAG_WORK] },
  { title: 'Rania Aziz', month: 8, day: 13, year: 2004, source: COLLECT, tags: [TAG_KIDS] },
  { title: 'Sven Berglund', month: 9, day: 2, year: 1943, source: ICS },
  { title: 'Tove Halvorsen', month: 9, day: 27, year: 1997, source: MANUAL, tags: [TAG_FRIENDS, TAG_WORK] },
  { title: 'Ulla Grimsdóttir', month: 10, day: 8, year: 1959, source: ICS, tags: [TAG_FAMILY] },
  { title: 'Viktor Petrov', month: 10, day: 31, year: 1982, source: COLLECT, tags: [TAG_FAMILY, TAG_WORK] },
  { title: 'Wilma Åkesson', month: 11, day: 15, year: 1995, source: MANUAL },
  { title: 'Xu Meilin', month: 11, day: 16, year: 1969, source: ICS },
  { title: 'Yara Costa', month: 12, day: 24, year: 2008, source: COLLECT },

  { title: 'Studio reopens', month: 1, day: 7, source: MANUAL, kind: 'note' },
  { title: 'Midsummer', month: 6, day: 19, source: MANUAL, kind: 'note' },
  { title: 'Retrospective, 2026 only', month: 10, day: 2, year: 2026, source: MANUAL, kind: 'note' },
];

function id(index: number): string {
  return `evt_sample${String(index).padStart(12, '0')}`;
}

export const sampleEvents: CalEvent[] = SEEDS.map((seed, i) => ({
  id: id(i),
  calendarId: CAL_ID,
  sourceId: seed.source,
  kind: seed.kind ?? 'birthday',
  month: seed.month,
  day: seed.day,
  ...(seed.year === undefined ? {} : { year: seed.year }),
  ...(seed.tags === undefined ? {} : { tagIds: seed.tags }),
  title: seed.title,
}));

/**
 * The three saved layouts of docs/CIRCLES.md §6, one per mode the root can
 * take. "Everyone" is the default and is what a calendar looks like before
 * anyone has made a view: one ring, the circles as tree rings on it. "Split"
 * gives each top-level circle a ring of its own on an orbit and keeps Kids a
 * band on Family rather than a fourth ring. "Concentric" nests them instead,
 * loudest outermost. Both say it per circle — `placement` on the child — as
 * the panel's control does; the root's mode is only the default for a circle
 * that has said nothing.
 */
export const sampleViews: View[] = [
  {
    id: 'vw_everyone00000000001',
    calendarId: CAL_ID,
    name: 'Everyone',
    layout: { nodes: { root: { mode: 'merged', visible: true } } },
    isDefault: true,
    sort: 0,
  },
  {
    id: 'vw_split0000000000001',
    calendarId: CAL_ID,
    name: 'Split',
    layout: {
      nodes: {
        root: { mode: 'merged', visible: true },
        [TAG_FAMILY]: { mode: 'merged', placement: 'orbit', visible: true },
        [TAG_FRIENDS]: { mode: 'merged', placement: 'orbit', visible: true },
        [TAG_WORK]: { mode: 'merged', placement: 'orbit', visible: true },
        [TAG_KIDS]: { mode: 'merged', placement: 'band', visible: true },
      },
    },
    isDefault: false,
    sort: 1,
  },
  {
    id: 'vw_concentric00000001',
    calendarId: CAL_ID,
    name: 'Concentric',
    layout: {
      nodes: {
        root: { mode: 'merged', visible: true },
        [TAG_FAMILY]: { mode: 'merged', placement: 'nested', visible: true },
        [TAG_FRIENDS]: { mode: 'merged', placement: 'nested', visible: true },
        [TAG_WORK]: { mode: 'merged', placement: 'nested', visible: true },
        [TAG_KIDS]: { mode: 'merged', placement: 'nested', visible: true },
      },
    },
    isDefault: false,
    sort: 2,
  },
];

export const sampleCalendar: Calendar = {
  id: CAL_ID,
  // Unclaimed: an anonymous calendar held by its owner token, like a fresh one.
  ownerId: null,
  name: 'Studio & family',
  theme: { name: 'default' },
  visibility: 'private',
  icsAlarm: 'none',
  createdAt: '2026-01-01T00:00:00.000Z',
  ownerTokenEnabled: true,
  claimed: false,
};

export const sampleBundle: CalendarBundle = {
  calendar: sampleCalendar,
  sources: sampleSources,
  events: sampleEvents,
  tags: sampleTags,
  views: sampleViews,
  eventPins: [],
};
